import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SendIcon from '@mui/icons-material/Send';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { format } from 'date-fns';

import {
  TRIGGER_SURVEY,
  COMPANY_SEARCH_QUERY,
  UPDATE_MEETING_CALL_TYPE,
  UPDATE_MEETING_COMPANY,
} from '../../graphql/meetings';

interface MeetingDetail {
  id: number;
  title?: string;
  meetingDate: string;
  endTime?: string;
  domain?: string;
  companyId?: number;
  companyName?: string;
  status?: string;
  source?: string;
  durationMinutes?: number;
  surveyEligible?: boolean;
  callType?: string;
  originationsCalendarInvited?: boolean;
  attendees?: { id: number; userId: number }[];
  externalAttendees?: { id: number; email: string; displayName?: string; domain?: string; companyName?: string }[];
  surveyResponses?: { id: number }[];
}

interface MeetingDetailDialogProps {
  open: boolean;
  onClose: () => void;
  meeting: MeetingDetail | null;
}

const MeetingDetailDialog: React.FC<MeetingDetailDialogProps> = ({ open, onClose, meeting }) => {
  const navigate = useNavigate();
  const [triggerSurvey, { loading: triggerLoading }] = useMutation(TRIGGER_SURVEY);
  const [updateCallType] = useMutation(UPDATE_MEETING_CALL_TYPE);
  const [updateCompany] = useMutation(UPDATE_MEETING_COMPANY);
  const [showCompanyOverride, setShowCompanyOverride] = useState(false);
  const [companySearchText, setCompanySearchText] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data: overrideCompanyData } = useQuery(COMPANY_SEARCH_QUERY, {
    variables: { query: companySearchText },
    skip: companySearchText.length < 2,
  });

  const handleCompanyOverride = async (companyId: number) => {
    try {
      const result = await updateCompany({ variables: { meetingId: meeting!.id, companyId } });
      if (result.data?.updateMeetingCompany?.ok) {
        setSnackbar({ open: true, message: 'Company updated', severity: 'success' });
        setShowCompanyOverride(false);
      } else {
        setSnackbar({
          open: true,
          message: result.data?.updateMeetingCompany?.error || 'Failed',
          severity: 'error',
        });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  if (!meeting) return null;

  const handleCallTypeChange = async (callType: string) => {
    try {
      const result = await updateCallType({ variables: { meetingId: meeting.id, callType } });
      if (result.data?.updateMeetingCallType?.ok) {
        setSnackbar({ open: true, message: `Call type set to ${callType}`, severity: 'success' });
      } else {
        setSnackbar({
          open: true,
          message: result.data?.updateMeetingCallType?.error || 'Failed',
          severity: 'error',
        });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const hasSurvey = meeting.surveyResponses && meeting.surveyResponses.length > 0;
  const meetingDate = new Date(meeting.meetingDate);
  const isPast = meetingDate < new Date();
  const isCancelled = meeting.status === 'cancelled';
  const canTrigger = isPast && !isCancelled;

  const handleTriggerSurvey = async () => {
    try {
      const result = await triggerSurvey({ variables: { meetingId: meeting.id } });
      const payload = result.data?.triggerSurvey;
      if (payload?.success) {
        setSnackbar({
          open: true,
          message: `${payload.notificationsSent} survey notification(s) sent`,
          severity: 'success',
        });
      } else {
        setSnackbar({ open: true, message: payload?.message || 'Failed to trigger survey', severity: 'error' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Error triggering survey', severity: 'error' });
    }
  };

  const getSurveyStatus = () => {
    if (hasSurvey) return { label: 'Completed', color: 'success' as const };
    if (isPast) return { label: 'Overdue', color: 'error' as const };
    return { label: 'Pending', color: 'warning' as const };
  };

  const surveyStatus = getSurveyStatus();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{meeting.title || meeting.domain || 'Meeting Details'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Date & Time
            </Typography>
            <Typography>
              {format(meetingDate, 'PPP p')}
              {meeting.endTime && ` - ${format(new Date(meeting.endTime), 'p')}`}
            </Typography>
          </Box>

          {meeting.durationMinutes && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Duration
              </Typography>
              <Typography>{meeting.durationMinutes} minutes</Typography>
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Company
            </Typography>
            {meeting.companyId ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <a
                  href={`/evaluation/dfm?companyId=${meeting.companyId}`}
                  style={{ color: '#3F5D66', fontWeight: 500 }}
                >
                  {meeting.companyName || meeting.domain}
                </a>
                {!showCompanyOverride && (
                  <Button size="small" variant="text" onClick={() => setShowCompanyOverride(true)}>
                    Change
                  </Button>
                )}
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography color="text.secondary">{meeting.domain || 'Unknown'}</Typography>
                {!showCompanyOverride && (
                  <Button size="small" variant="outlined" onClick={() => setShowCompanyOverride(true)}>
                    Link company
                  </Button>
                )}
              </Stack>
            )}
            {showCompanyOverride && (
              <Box sx={{ mt: 1 }}>
                <Autocomplete
                  options={overrideCompanyData?.searchCompanies ?? []}
                  getOptionLabel={(opt: any) => `${opt.companyName} (${opt.domain})`}
                  inputValue={companySearchText}
                  onInputChange={(_e, val) => setCompanySearchText(val)}
                  onChange={(_e, val: any) => {
                    if (val) handleCompanyOverride(val.id);
                  }}
                  renderInput={params => <TextField {...params} label="Search company" size="small" autoFocus />}
                  noOptionsText={companySearchText.length < 2 ? 'Type to search...' : 'No results'}
                />
                <Button size="small" onClick={() => setShowCompanyOverride(false)} sx={{ mt: 0.5 }}>
                  Cancel
                </Button>
              </Box>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Call Type
            </Typography>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select value={meeting.callType || ''} displayEmpty onChange={e => handleCallTypeChange(e.target.value)}>
                <MenuItem value="" disabled>
                  Not set
                </MenuItem>
                <MenuItem value="initial">Initial (needs survey)</MenuItem>
                <MenuItem value="follow_up">Follow-up</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Survey Status
            </Typography>
            <Chip label={surveyStatus.label} color={surveyStatus.color} size="small" />
          </Box>

          {meeting.source && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Source
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip label={meeting.source} size="small" variant="outlined" />
                {meeting.originationsCalendarInvited && (
                  <Chip label="Originations calendar" size="small" color="primary" variant="outlined" />
                )}
              </Stack>
            </Box>
          )}

          <Divider />

          {meeting.attendees && meeting.attendees.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Internal Attendees
              </Typography>
              <Typography>{meeting.attendees.length} attendee(s)</Typography>
            </Box>
          )}

          {meeting.externalAttendees && meeting.externalAttendees.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                External Attendees
              </Typography>
              {meeting.externalAttendees.map(ea => (
                <Typography key={ea.id} variant="body2">
                  {ea.displayName || ea.email}
                  {ea.companyName && ` (${ea.companyName})`}
                </Typography>
              ))}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="outlined"
          startIcon={<SendIcon />}
          disabled={!canTrigger || triggerLoading}
          onClick={handleTriggerSurvey}
        >
          Send Survey
        </Button>
        {hasSurvey ? (
          <Button
            variant="outlined"
            onClick={() => {
              onClose();
              navigate(`/survey/${meeting.id}`);
            }}
          >
            View Survey
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => {
              onClose();
              navigate(`/survey/${meeting.id}`);
            }}
          >
            Fill Survey
          </Button>
        )}
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default MeetingDetailDialog;
